// First-entry disclosure + informed-consent gate (spec §7), and the "stopped" screen
// shown after a decline or a withdrawal.
//
// Presented as a centered modal card over a dimmed backdrop — the same pattern as the
// content-notice interstitial (dim + blur, `lensfade`/`lenspop`, card scrolls internally).
// Two modes over ONE document, so a participant reviewing later reads exactly the text
// they agreed to:
//   • mode="entry"  — the gate. Nothing but the app masthead sits behind it (App does not
//                     mount <Lens/> yet), so no stimulus is visible or logged. Four
//                     affirmative checkboxes (eligibility · AI-rewrite disclosure ·
//                     content notice · recording + logging) unlock "I agree"; "I do not
//                     agree" ends the session. No dismiss — a decision is required.
//   • mode="review" — the same document reopened from "Study info" in the top bar, over
//                     the live app, with the agreement stamp and a withdraw control
//                     instead of the checkboxes. Esc / click-away / × close it.
//
// It is study chrome, identical in every condition (§0: conditions differ only in which
// panels are visible), and is shown BEFORE any stimulus is mounted or logged.

import { useEffect, useRef, useState } from "react";
import type { Telemetry } from "../telemetry";
import { INK, INK_DEEP, SURFACE, sectionLabel } from "../style";
import { CONSENT_VERSION, STUDY, formatAgreedAt, type ConsentRecord } from "../consent";

const MUTED = "rgba(55,53,47,.72)";
const FAINT = "rgba(55,53,47,.5)";

export function ConsentGate({ mode, record, tel, onAgree, onDecline, onWithdraw, onClose }: {
  mode: "entry" | "review";
  record?: ConsentRecord | null;
  tel: Telemetry;
  onAgree?: () => void;
  onDecline?: () => void;
  onWithdraw?: () => void;
  onClose?: () => void;
}) {
  const [checks, setChecks] = useState({ age: false, ai: false, content: false, data: false });
  const opened = useRef<number>(Date.now());
  const root = useRef<HTMLDivElement | null>(null);
  const all = checks.age && checks.ai && checks.content && checks.data;
  // identity line — whatever of institution/PI/protocol is filled in, nothing if none
  const byline = [STUDY.org, STUDY.pi, STUDY.irb_protocol].filter(Boolean).join(" · ");

  useEffect(() => {
    tel.log("consent_view", { version: CONSENT_VERSION, mode });
    root.current?.focus(); // move focus into the dialog
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // review mode: Esc closes (entry mode has no dismiss — a decision is required)
  useEffect(() => {
    if (mode !== "review" || !onClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, onClose]);

  const agree = () => {
    if (!all) return;
    tel.log("consent_agree", { version: CONSENT_VERSION, ms_on_page: Date.now() - opened.current });
    onAgree?.();
  };
  const decline = () => {
    tel.log("consent_decline", { version: CONSENT_VERSION, ms_on_page: Date.now() - opened.current });
    onDecline?.();
  };
  const withdraw = () => {
    tel.log("consent_withdraw", { version: CONSENT_VERSION });
    onWithdraw?.();
  };

  return (
    <Modal standalone={mode === "entry"} onClickAway={mode === "review" ? onClose : undefined}>
      <div ref={root} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="consent-title"
        className="lenspop" onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 620, maxHeight: "min(88vh, 880px)", display: "flex", flexDirection: "column",
          background: "#fff", borderRadius: 6, borderTop: "3px solid " + INK, outline: "none",
          boxShadow: "0 16px 48px rgba(15,15,15,.2)", color: INK, fontFamily: "'Source Sans 3',sans-serif",
        }}>

        {/* ---- card head (fixed) ---- */}
        <div style={{ flex: "none", padding: "24px 28px 14px", borderBottom: "1px solid rgba(55,53,47,.09)" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <div style={{ ...sectionLabel, letterSpacing: ".07em", marginBottom: 7 }}>
              Research study · {mode === "review" ? "study information" : "informed consent"}
            </div>
            <div style={{ flex: 1 }} />
            {mode === "review" && (
              <button onClick={onClose} aria-label="Close"
                style={{ font: "inherit", fontSize: 17, lineHeight: 1, color: FAINT, background: "transparent", border: "none", cursor: "pointer", padding: "0 2px" }}>×</button>
            )}
          </div>
          <h1 id="consent-title" style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 23, fontWeight: 700, lineHeight: 1.22, letterSpacing: "-.012em", margin: 0, color: INK_DEEP, textWrap: "balance" as any }}>
            {STUDY.title}
          </h1>
          {byline && (
            <p style={{ margin: "7px 0 0", fontSize: 13, color: FAINT, lineHeight: 1.5 }}>{byline}</p>
          )}
        </div>

        {/* ---- the document (scrolls) ---- */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", padding: "20px 28px 26px" }}>
        <Section title="What this study is about">
          We built a research prototype, <strong>Incident Lens</strong>, that marks up and
          rewrites news coverage of real police use-of-force incidents to show how differently
          outlets can frame the same event. This session is a <strong>design study</strong>: we
          want your honest reactions so we can find the prototype's gaps, risks, and what to
          improve. We are not testing you and we are not measuring your opinions about policing:
          it is the system being evaluated, and critical feedback is the most useful kind.
        </Section>

        <Section title="What you will do">
          A researcher will walk you through the prototype for {STUDY.duration}. You'll read a news
          story about a police use-of-force incident as it was published, then again with the
          prototype's tools switched on one at a time: marked-up phrases with explanations,
          comparisons with how other outlets covered the same incident, an AI-written neutral
          rewrite shown as tracked changes, and controls that re-slant the story toward a chosen
          framing. Throughout, we'll ask you to <strong>think out loud</strong> and answer open
          questions about what you notice. There are no right answers.
        </Section>

        <Callout tone="ai" title="Some of the text you will see was written by AI">
          The prototype shows a version of the article that an <strong>AI system rewrote</strong>{" "}
          to reduce slanted framing, displayed as tracked changes over the original, and later in
          the session lets you push the article <em>toward</em> a chosen framing instead.
          AI-written text is <strong>study material, not journalism</strong>: it was not produced
          or endorsed by the outlet named, it is not a correction of that outlet's reporting, and
          it should not be quoted, shared, or treated as an account of what happened. It is
          labeled wherever it appears, and the original article always stays visible next to it.
        </Callout>

        <Callout tone="warn" title="The stories describe real violence">
          The articles are real news reports about real incidents in which police used force;
          some were fatal. They include descriptions of shootings and death, and may mention
          race, mental illness, and criminal history. You can leave any single story with the{" "}
          <strong>Skip story</strong> control in the top bar, and you can pause or stop the
          session at any time. If you need support: {STUDY.support}.
        </Callout>

        <Section title="What we record">
          Three things. <strong>(1)</strong> The session is <strong>recorded</strong> (audio and
          screen) and transcribed, so we can analyze what you said. <strong>(2)</strong> The
          prototype logs <strong>how you interact with it</strong>: which panels and explanations
          you open, what you click, how long you spend, how far you scroll. These logs are tied to
          a random session code stored in this browser, not to your name. <strong>(3)</strong> The
          researcher takes notes.
        </Section>

        <Section title="What happens to it">
          Recordings, transcripts, and notes are kept by the research team and are not shared
          outside it. When we write up what we learned, we may <strong>quote you, but without
          your name</strong>, employer, or anything else that identifies you. Interaction logs are
          analyzed in aggregate, and may be released de-identified so other researchers can
          reproduce our analysis. With your permission, we may email you afterwards with a
          follow-up question.
        </Section>

        <Section title="Taking part is voluntary">
          You can skip any question, ask to pause or stop the recording, or end the session at any
          point, with no explanation needed and no penalty. You can reopen this page any time from{" "}
          <strong>Study info</strong> in the top bar and withdraw there, which stops the prototype
          from logging anything further. If you later want your interview data deleted, tell the
          researcher.
        </Section>

        <Section title="Risks, benefits, compensation">
          The main risk is discomfort from the subject matter. Because the session is recorded
          there is a small risk to confidentiality; we limit it by keeping recordings within the
          research team and anonymizing anything we quote. There is no direct benefit to you. Any
          compensation is arranged with the researcher.
        </Section>

        {(STUDY.pi_email || STUDY.irb_contact) && (
          <Section title="Questions">
            {STUDY.pi_email && <>About the study: {STUDY.pi ? STUDY.pi + " " : ""}&lt;{STUDY.pi_email}&gt;. </>}
            {STUDY.irb_contact && <>About your rights as a research participant: {STUDY.irb_contact}
              {STUDY.irb_protocol ? " (" + STUDY.irb_protocol + ")" : ""}.</>}
          </Section>
        )}

        {/* ---- decision ---- */}
        {mode === "entry" ? (
          <div style={{ marginTop: 30, borderTop: "1px solid rgba(55,53,47,.12)", paddingTop: 22 }}>
            <div style={{ ...sectionLabel, letterSpacing: ".07em", marginBottom: 12 }}>Please confirm</div>
            <Check id="c-age" checked={checks.age} onChange={(v) => setChecks((c) => ({ ...c, age: v }))}>
              I am 18 or older.
            </Check>
            <Check id="c-ai" checked={checks.ai} onChange={(v) => setChecks((c) => ({ ...c, ai: v }))}>
              I understand that some of the text in this prototype was written by an AI system and
              is not the outlet's reporting.
            </Check>
            <Check id="c-content" checked={checks.content} onChange={(v) => setChecks((c) => ({ ...c, content: v }))}>
              I have read the content notice above and I'm willing to read coverage of a police
              use-of-force incident.
            </Check>
            <Check id="c-data" checked={checks.data} onChange={(v) => setChecks((c) => ({ ...c, data: v }))}>
              I agree to take part, to this session being recorded and transcribed, and to my
              interactions with the prototype being logged as described above.
            </Check>

            <div style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap" }}>
              <button onClick={agree} disabled={!all}
                style={{ fontFamily: "inherit", fontSize: 14.5, fontWeight: 600, padding: "12px 22px", borderRadius: 5, border: "none", background: all ? INK : "#e7e6e1", color: all ? "#fff" : "rgba(55,53,47,.42)", cursor: all ? "pointer" : "not-allowed", transition: "background .12s" }}>
                I agree and start the session
              </button>
              <button onClick={decline}
                style={{ fontFamily: "inherit", fontSize: 14.5, fontWeight: 500, padding: "12px 20px", borderRadius: 5, border: "1px solid rgba(55,53,47,.16)", background: "#fff", color: "rgba(55,53,47,.7)", cursor: "pointer" }}>
                I do not agree
              </button>
            </div>
            {!all && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: FAINT }}>
                Check all four boxes to continue.
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 30, borderTop: "1px solid rgba(55,53,47,.12)", paddingTop: 22 }}>
            <div style={{ fontSize: 13.5, color: MUTED, lineHeight: 1.6 }}>
              {record
                ? <>You agreed to this form on <strong>{formatAgreedAt(record.agreed_at)}</strong> (version {record.version}). Your participant code is <code style={mono}>{record.participant_id}</code>.</>
                : <>No agreement is recorded in this browser.</>}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
              <button onClick={onClose}
                style={{ fontFamily: "inherit", fontSize: 14.5, fontWeight: 600, padding: "11px 20px", borderRadius: 5, border: "none", background: INK, color: "#fff", cursor: "pointer" }}>
                Back to the study
              </button>
              <button onClick={withdraw}
                style={{ fontFamily: "inherit", fontSize: 14.5, fontWeight: 500, padding: "11px 18px", borderRadius: 5, border: "1px solid rgba(55,53,47,.16)", background: "#fff", color: "rgba(55,53,47,.7)", cursor: "pointer" }}>
                Withdraw and stop recording
              </button>
            </div>
          </div>
        )}

        <div style={{ marginTop: 22, fontSize: 11.5, color: "rgba(55,53,47,.38)" }}>
          Consent text version {CONSENT_VERSION}.
        </div>
        </div>
      </div>
    </Modal>
  );
}

/** After "I do not agree" or a withdrawal — a dead end, by design. Same modal shell. */
export function ConsentStopped({ reason, onReview }: { reason: "declined" | "withdrawn"; onReview: () => void }) {
  return (
    <Modal standalone>
      <div role="dialog" aria-modal="true" className="lenspop"
        style={{ width: "100%", maxWidth: 440, background: "#fff", borderRadius: 6, borderTop: "3px solid " + INK, boxShadow: "0 16px 48px rgba(15,15,15,.2)", padding: "30px 30px 26px", textAlign: "center", fontFamily: "'Source Sans 3',sans-serif", color: INK }}>
        <div style={{ fontSize: 26, marginBottom: 10, color: FAINT }}>◦</div>
        <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 21, fontWeight: 700, margin: "0 0 10px", lineHeight: 1.24 }}>
          {reason === "withdrawn" ? "You withdrew from the study" : "You chose not to take part"}
        </h2>
        <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.65, margin: "0 0 22px" }}>
          {reason === "withdrawn"
            ? "The prototype has stopped logging and your consent record was deleted. Tell the researcher if you'd also like the session recording stopped or your data deleted."
            : "Nothing was logged except that someone declined at this point: no reading or interaction data. Let the researcher know how you'd like to continue."}
        </p>
        <button onClick={onReview}
          style={{ fontFamily: "inherit", fontSize: 14, fontWeight: 600, padding: "10px 20px", borderRadius: 5, border: "1px solid rgba(55,53,47,.16)", background: "#fff", color: "rgba(55,53,47,.72)", cursor: "pointer" }}>
          Read the consent form again
        </button>
      </div>
    </Modal>
  );
}

// ---- small pieces ----

/** Dim + blur backdrop holding one centered card (the content-notice interstitial's
 *  pattern). `standalone` = nothing is mounted behind it, so paint an empty app shell
 *  (masthead only — never a stimulus) under the dim layer. */
function Modal({ standalone, onClickAway, children }: {
  standalone?: boolean; onClickAway?: () => void; children: React.ReactNode;
}) {
  return (
    <>
      {standalone && (
        <div style={{ position: "fixed", inset: 0, zIndex: 98, background: "#fff" }}>
          <div style={{ borderBottom: "1px solid rgba(55,53,47,.1)", padding: "10px 22px", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: INK, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
              <div style={{ width: 12, height: 12, border: "2px solid #fff", borderRadius: "50%", position: "relative" }}>
                <div style={{ position: "absolute", width: 5, height: 5, background: "#fff", borderRadius: "50%", top: 1.5, left: 1.5 }} />
              </div>
            </div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15.5, fontWeight: 700, lineHeight: 1, letterSpacing: "-.01em", color: INK }}>Incident Lens</div>
          </div>
        </div>
      )}
      <div className="lensfade" onClick={onClickAway}
        style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(45,44,40,.34)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, overflow: "hidden" }}>
        {children}
      </div>
    </>
  );
}

const mono: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono',ui-monospace,monospace", fontSize: 12,
  background: SURFACE.chip, padding: "1px 5px", borderRadius: 3,
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 20 }}>
      <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, fontWeight: 700, margin: "0 0 6px", letterSpacing: "-.005em" }}>{title}</h2>
      <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.68, color: MUTED }}>{children}</p>
    </section>
  );
}

/** Emphasized disclosure blocks — the two things a reader must not miss. */
function Callout({ tone, title, children }: { tone: "ai" | "warn"; title: string; children: React.ReactNode }) {
  const accent = tone === "warn" ? "#b3261e" : INK;
  return (
    <section style={{ margin: "0 0 20px", background: SURFACE.warmer, border: "1px solid rgba(55,53,47,.12)", borderLeft: "3px solid " + accent, borderRadius: 5, padding: "16px 18px" }}>
      <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, fontWeight: 700, margin: "0 0 6px", letterSpacing: "-.005em", color: tone === "warn" ? accent : INK_DEEP }}>{title}</h2>
      <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.68, color: MUTED }}>{children}</p>
    </section>
  );
}

function Check({ id, checked, onChange, children }: {
  id: string; checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode;
}) {
  return (
    <label htmlFor={id}
      style={{ display: "flex", gap: 11, alignItems: "flex-start", padding: "9px 12px", marginBottom: 8, borderRadius: 5, cursor: "pointer", background: checked ? SURFACE.warm : "#fff", border: "1px solid " + (checked ? "rgba(55,53,47,.2)" : "rgba(55,53,47,.12)"), transition: "background .12s, border-color .12s" }}>
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 3, width: 15, height: 15, accentColor: INK, flex: "none", cursor: "pointer" }} />
      <span style={{ fontSize: 14, lineHeight: 1.55, color: checked ? INK : MUTED }}>{children}</span>
    </label>
  );
}
